import { Key } from 'react';
import { AxiosInstance } from 'axios';
import { jsonrepair } from 'jsonrepair';

import { CommonDownloadableFile } from './common.types';

export async function handleDownloadFile<T extends CommonDownloadableFile>(
  file: T,
  client: AxiosInstance,
) {
  const response = await client.get(file.url, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data]);
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `${file.type}.${file.extension}`;
  link.click();
}

export function tryJsonParse<T>(jsonString: string | null | undefined): T | null {
  if (!jsonString) return null;

  try {
    const trimmedJsonString = jsonString.trim();
    if (trimmedJsonString === '') return null;

    const sanitizedString = jsonrepair(trimmedJsonString);
    return JSON.parse(sanitizedString) as T;
  } catch (error) {
    console.log('Failed to parse string json: ', { value: jsonString, error });
    return null;
  }
}

export function formatToCurrency({
  value = 0,
  currency = 'AUD',
  locale = 'en-AU',
}: {
  value?: number | null;
  currency?: string;
  locale?: string;
}): string {
  const defaultValue = value ?? 0;
  const formattedNumber = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    signDisplay: 'never',
  }).format(defaultValue);

  return defaultValue < 0 ? `(${formattedNumber})` : formattedNumber;
}

export function validateWhitespacesAsEmpty(value?: string | null) {
  if (!value) return 'This field is required';
  const valid = value.trim().length > 0;
  return valid || 'This field is required';
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function parseValueFromKey(key: Key) {
  if (key === '') return key;
  const parsedValue = Number(key);
  return isNaN(parsedValue) ? key.toString() : parsedValue;
}

export function areSetsEqual(a: Set<Key>, b: Set<Key>): boolean {
  return a.size === b.size && [...a].every((item) => b.has(item));
}

export function areArraysEqual(a: Key[], b: Key[]): boolean {
  return (
    a.length === b.length &&
    a.every((item, i) => parseValueFromKey(item) === parseValueFromKey(b[i]))
  );
}

/**
 * @deprecated use isNumber from @xemplo/type-guard
 */
export function isNumber(value: unknown | undefined): value is number {
  return value != null && value !== '' && !isNaN(Number(value));
}

export function isSafeNumber(value: unknown): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && Number.isSafeInteger(value)
  );
}

export function isString(value: unknown | undefined): value is string {
  if (value == null) return false;
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * @deprecated use isFunction from @xemplo/type-guard
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFn = (...args: any[]) => any;
export function isFunction<TFn = AnyFn>(value: unknown | undefined): value is TFn {
  if (value == null) return false;
  return typeof value === 'function';
}

/**
 * @deprecated use isObject from @xemplo/type-guard
 */
export function isObject(value: unknown | undefined): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @deprecated use isBoolean from @xemplo/type-guard
 */
export function isBoolean(value: unknown | undefined): value is boolean {
  return typeof value === 'boolean';
}

/**
 * Helper that ensures the return value is either a valid non-empty string or undefined.
 * @returns Trimmed string or undefined
 */
export function strictString(value?: string | null): string | undefined {
  if (value == null) return undefined;
  return isString(value) ? value.trim() : undefined;
}

/**
 * @deprecated use isSSR from @xemplo/type-guard
 * Checks if the code is running in a server-side rendering (SSR) environment.
 * @returns {boolean} True if `window` or `document` is undefined, indicating SSR; otherwise, false.
 */
export function isSSR(): boolean {
  return typeof window === 'undefined' || typeof document === 'undefined';
}

/**
 * Checks if the DOM is available by verifying that both `document` and `window` are defined.
 * This is useful in server-side rendering environments where these globals may not exist.
 *
 * Virtually the same purpose as isSSR but inverted and more semantic usage.
 *
 * @returns {boolean} True if both `document` and `window` are defined, false otherwise.
 */
export function isDomAvailable() {
  return typeof document !== 'undefined' && typeof window !== 'undefined';
}
